import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { CompanyService } from 'src/app/company/company.service';
import { Store } from '@ngrx/store';
import * as ValidationSummaryActions from 'src/app/company/validation-summary/data-access/validation-summary-actions';
import { selectValidationSummaryData } from 'src/app/company/validation-summary/data-access/validation-summary-selectors';

@Injectable({ providedIn: 'root' })
export class SubmissionSummaryService {
  constructor(
    private readonly companyService: CompanyService,
    private readonly router: Router,
    private readonly store?: Store
  ) {}
  loadPremiumDetails(
    prfNumber: string,
    currentYear: number,
    groupCode: string
  ): Observable<any> {
    return this.companyService
      .getPremiumReportData(prfNumber, currentYear, groupCode)
      .pipe(
        map((resp) => {
          if (resp['message'] == 'Success') {
            const total_direct_premium = this.getLastPremiumValue(
              resp['data']['data']['direct_premium']
            );
            const total_supplemental_premium = this.getLastPremiumValue(
              resp['data']['data']['supplemental_premium']
            );
            const ud_total_premium = this.getLastPremiumValue(
              resp['data']['userdefined_premium']['dpremium']
            );
            const userdefined_premium_supplemental = this.getLastPremiumValue(
              resp['data']['userdefined_premium']['spremium']
            );
            const userdefined_premium =
              ud_total_premium - userdefined_premium_supplemental;
            const supplemental_premium =
              total_supplemental_premium - userdefined_premium_supplemental;
            const total = total_supplemental_premium + userdefined_premium;
            const direct_premium = total_direct_premium - total;
            const total_premium = total_direct_premium;
            return {
              direct_premium,
              supplemental_premium,
              userdefined_premium,
              userdefined_premium_supplemental,
              total_premium,
            };
          }
          return null;
        }),
        catchError((error) => {
          this.router.navigate(['/apifailure']);
          return of(null);
        })
      );
  }
  getLastPremiumValue(data): any {
    if (data && data.length > 0) {
      const lastRow = data[data.length - 1];
      const keys = Object.keys(lastRow);
      const lastKey = keys[keys.length - 1];
      return lastRow[lastKey];
    }
    return null;
  }
  loadCompanyInfo(
    currentYear: number,
    prfNumber: string,
    groupCode: string
  ): Observable<any> {
    return this.companyService
      .loadCompanyInformation(currentYear, prfNumber, groupCode)
      .pipe(
        map((resp) => {
          if (resp['message'] == 'Success') {
            return resp['companydata'];
          }
          return null;
        }),
        catchError((error) => {
          this.router.navigate(['/apifailure']);
          return of(null);
        })
      );
  }

  getAnnualStatement(currentYear: number, prfNumber: string): Observable<any> {
    return this.companyService.getAnualStatement(currentYear, prfNumber).pipe(
      map((resp) => {
        if (resp && resp['message'] == 'Success') {
          return resp['data']['mappingdetails'];
        }
        return null;
      }),
      catchError((error) => {
        this.router.navigate(['/apifailure']);
        return of(null);
      })
    );
  }

  getIsoLines(currentYear: number, prfNumber: string): Observable<any> {
    return this.companyService.getIsoLines(currentYear, prfNumber).pipe(
      map((isoResp) => {
        if (isoResp['message'] == 'Success') {
          return isoResp['isolineslist'];
        }
        return null;
      }),
      catchError((error) => {
        this.router.navigate(['/apifailure']);
        return of(null);
      })
    );
  }

  loadAnnualStatementComparison(
    currentYear: number,
    groupCode: string,
    prfNumber: string
  ): Observable<any> {
    return this.companyService
      .loadAnnualStatementComparison(currentYear, groupCode, prfNumber)
      .pipe(
        map((resp) => {
          if (resp['message'] == 'Success') {
            return resp['ascomparisondata'];
          }
          return null;
        }),
        catchError((error) => {
          this.router.navigate(['/apifailure']);
          return of(null);
        })
      );
  }

  getComments(
    currentYear: number,
    prfNumber: string,
    groupCode: string
  ): Observable<any> {
    return this.companyService
      .getComments(currentYear, prfNumber, groupCode)
      .pipe(
        map((resp) => {
          if (resp['message'] == 'Success') {
            return resp['comment'];
          }
          return null;
        }),
        catchError((error) => {
          this.router.navigate(['/apifailure']);
          return of(null);
        })
      );
  }

  loadUserdefinedColumns(
    currentYear: number,
    prfNumber: string
  ): Observable<any> {
    return this.companyService
      .getUserdefinedColumns(currentYear, prfNumber)
      .pipe(
        map((resp) => {
          if (resp['message'] == 'Success') {
            return resp['udeflobdetails']['lobdetails'];
          }
          return null;
        }),
        catchError((error) => {
          this.router.navigate(['/apifailure']);
          return of(null);
        })
      );
  }

  loadGroupReporting(
    currentYear: number,
    prfNumber: string,
    groupCode: string
  ): Observable<any> {
    return this.companyService
      .loadGroupReport(currentYear, prfNumber, groupCode)
      .pipe(
        map((resp) => {
          if (resp['message'] == 'Success') {
            return {
              groupcompanydetails: resp['groupcompanydetails'],
              groupreporting: resp['groupreporting'],
            };
          }
          return null;
        }),
        catchError((error) => {
          this.router.navigate(['/apifailure']);
          return of(null);
        })
      );
  }

  loadValidationSummaryData() {
    if (this.store) {
      this.store.dispatch(
        ValidationSummaryActions.loadAllValidationSummaryItems()
      );
    }
  }

  selectValidationSummaryData() {
    if (this.store) {
      return this.store.select(selectValidationSummaryData);
    }
    return null;
  }

  getSelectedIsoLine(isolineslist: any): any {
    let isoline = '';
    let isolines = isolineslist['isolines'];
    const selectedisoline = isolineslist['selindex'];
    if (selectedisoline) {
      isoline = isolines.filter((i) => i.Number == selectedisoline)[0].Value;
    }
    return isoline;
  }

  getGroupReportingIncludedList(groupcompanydetails: any): any {
    return groupcompanydetails.filter(
      (company: { chkvalue: string }) => company.chkvalue == 'true'
    );
  }
}
